import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, MapPin, User } from "@phosphor-icons/react";

// Default centre: Saint-Aunès, 34 (BKPRO HQ area)
const DEFAULT_CENTER = [43.6108, 3.9662];

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `il y a ${Math.round(diff)} s`;
  if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
  return `il y a ${Math.round(diff / 86400)} j`;
}

export default function TeamMapDialog({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/team-locations");
      setRows(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [open]);

  const withPos = useMemo(() => rows.filter((r) => r.latitude != null && r.longitude != null), [rows]);
  const center = useMemo(
    () => (withPos.length > 0 ? [withPos[0].latitude, withPos[0].longitude] : DEFAULT_CENTER),
    [withPos],
  );
  const liveCount = withPos.filter((r) => r.is_live).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] p-0 bg-[#111] border-white/10 text-white rounded-2xl flex flex-col overflow-hidden" data-testid="team-map-dialog">
        <DialogHeader className="px-4 pt-4 pb-2 shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-display flex items-center gap-2">
              <MapPin size={20} className="text-emerald-400" weight="duotone" />
              Carte équipe
              <span className="text-[10px] uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5 ml-1">
                {liveCount} en ligne
              </span>
            </DialogTitle>
            <Button variant="ghost" onClick={load} disabled={loading} className="h-9 border border-white/10 text-xs" data-testid="team-map-refresh">
              <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Rafraîchir
            </Button>
          </div>
        </DialogHeader>

        {/* Map — capped so the team list below is always visible without scrolling */}
        <div className="shrink-0 relative bg-[#0A0A0A] h-[45vh] min-h-[280px]">
          <MapContainer
            center={center}
            zoom={11}
            className="w-full h-full"
            data-testid="team-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {withPos.map((r) => (
              <CircleMarker
                key={r.id}
                center={[r.latitude, r.longitude]}
                radius={10}
                pathOptions={{
                  color: r.color || "#005CFF",
                  fillColor: r.color || "#005CFF",
                  fillOpacity: r.is_live ? 0.85 : 0.35,
                  weight: r.is_live ? 3 : 1,
                }}
              >
                <Tooltip direction="top" offset={[0, -8]} opacity={1}>
                  <span style={{ color: "#111" }}>{r.name}</span>
                </Tooltip>
                <Popup>
                  <div style={{ minWidth: 160, color: "#111" }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{r.name}</div>
                    <div style={{ fontSize: 12, opacity: 0.7, textTransform: "capitalize" }}>{r.role}</div>
                    <div style={{ fontSize: 11, marginTop: 6 }}>
                      {r.is_live ? <span style={{ color: "#059669" }}>● En ligne</span> : <span style={{ color: "#B45309" }}>● Hors ligne</span>}
                      <br />
                      Vu {timeAgo(r.updated_at)}
                    </div>
                    <a
                      href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 11, color: "#005CFF", marginTop: 6, display: "inline-block" }}
                    >
                      Ouvrir dans Maps
                    </a>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>

        {/* Team list — takes the remaining space and only scrolls internally
            if the team is huge. On a normal 3-5 tech team the whole list is
            visible without any scrolling. */}
        <div className="flex-1 min-h-0 overflow-y-auto border-t border-white/10 bg-[#111]">
          <div className="sticky top-0 bg-[#111]/95 backdrop-blur px-4 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest text-white/50">
            Équipe · {rows.length} membre{rows.length > 1 ? "s" : ""}
          </div>
          {rows.length === 0 ? (
            <div className="p-4 text-center text-xs text-white/50">
              Aucune position partagée pour le moment. Les techniciens partagent leur position à chaque notification d&apos;arrivée client ou automatiquement quand l&apos;app est ouverte (permission requise).
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center gap-2 px-4 py-2.5" data-testid={`team-row-${r.id}`}>
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: r.color || "#005CFF", boxShadow: r.is_live ? `0 0 8px ${r.color || "#005CFF"}` : "none" }}
                  />
                  <User size={12} className="text-white/40" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{r.name}</div>
                    <div className="text-[10px] text-white/40 capitalize">{r.role} · {timeAgo(r.updated_at)}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${r.is_live ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/40"}`}>
                    {r.is_live ? "Live" : "Hors ligne"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
